import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { BlockType, BlockModel } from '@empathy/survey/base';

import { BlockTypeIconConfig } from '../../../models/blocks/block-type-config.constants';
import { QuestionEditor } from '../../../models/question-editor.model';
import { SurveyEditorService } from '../../../services/survey-editor.service';
import { ListQuestionBlockQuestion, ListQuestionBlockView } from '../list-questions/list-question-render-row.model';

@Component({
  selector: 'empathy-list-block-questions-item',
  templateUrl: 'list-block-item.component.html',
  styleUrls: ['./list-block-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListBlockQuestionsItemComponent {
  block = input<BlockModel>();
  blockView = input<ListQuestionBlockView>();
  activeQuestionId = input<string>();

  activate = output<string>();
  removePage = output<string>();

  block$ = computed(() => this.blockView()?.block ?? this.block());
  childBlockViews$ = computed(() => this.blockView()?.childBlocks ?? []);
  childBlocks$ = computed(() => {
    if (this.blockView()) {
      return [];
    }

    if (!this.isParentBlock$()) {
      return [];
    }
    // Note: Creating a new instance of the child blocks and list to trigger computed actions
    return this.surveyEditorService
      .surveyEditor$()
      .blocks.all.value.filter((b) => this.block().groupedBlockIds.includes(b.id))
      .map((b) => ({ ...b }) as BlockModel)
      .slice();
  });
  isParentBlock$ = computed(() => this.block$().type === BlockType.MultiBlock);

  questionsInBlock$ = computed<ListQuestionBlockQuestion[]>(() => {
    if (this.blockView()) {
      return this.blockView().questions;
    }

    const surveyEditor = this.surveyEditorService.surveyEditor$();
    const block = surveyEditor.blocks.byId(this.block$().id);

    return surveyEditor.questions
      .filter((q) => block.groupedQuestionIds.includes(q.id))
      .map((q) => ({
        isFirst: surveyEditor.questions.findIndex((question) => question.id === q.id) === 0,
        isLast: surveyEditor.questions.findIndex((question) => question.id === q.id) === surveyEditor.questions.length - 1,
        movePositionAllowed: surveyEditor.questions.length > 1,
        question: { ...q, active: false } as QuestionEditor,
        showPageBreak: false
      }))
      .slice();
  });

  blockTypeConfig = BlockTypeIconConfig;
  faClose = faClose;

  private surveyEditorService = inject(SurveyEditorService);
}
